import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { QuoteForm } from "@/components/QuoteForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Scissors,
  Leaf,
  Sprout,
  TreeDeciduous,
  Recycle,
  Flower2,
  Check,
  Star,
  Shield,
  Clock,
  Award,
  Users,
  Phone,
  ArrowRight,
} from "lucide-react";

const HomePage = () => {
  const services = [
    { icon: Scissors, title: "Lawn Mowing", desc: "Weekly & bi-weekly professional mowing services" },
    { icon: Leaf, title: "Lawn Maintenance", desc: "Complete care including edging, trimming & cleanup" },
    { icon: Sprout, title: "Weed Control", desc: "Effective weed prevention and removal treatments" },
    { icon: TreeDeciduous, title: "Fertilization", desc: "Custom fertilization programs for lush growth" },
    { icon: Recycle, title: "Yard Cleanup", desc: "Seasonal cleanups and debris removal" },
    { icon: Flower2, title: "Landscaping", desc: "Design and maintenance for beautiful yards" },
  ];

  const benefits = [
    { icon: Shield, title: "Licensed & Insured", desc: "Fully protected for your peace of mind" },
    { icon: Clock, title: "Reliable Service", desc: "Always on time, every time" },
    { icon: Award, title: "Quality Guaranteed", desc: "100% satisfaction on every job" },
    { icon: Users, title: "Expert Team", desc: "Trained professionals who care" },
  ];

  const testimonials = [
    { name: "Sarah M.", location: "Houston, TX", text: "Nexivas transformed my backyard into a beautiful oasis. Their attention to detail is remarkable!", rating: 5 },
    { name: "Robert K.", location: "Sugar Land, TX", text: "Best lawn care service I've ever used. Reliable, professional, and my lawn has never looked better.", rating: 5 },
    { name: "Jennifer L.", location: "Katy, TX", text: "They've been maintaining our commercial property for 2 years. Always impressed with their work.", rating: 5 },
  ];

  const faqs = [
    { q: "How often should I have my lawn mowed?", a: "For most Houston lawns, we recommend weekly mowing during the growing season (March-October) and bi-weekly during cooler months. We'll assess your lawn and provide a customized schedule." },
    { q: "Do you offer free estimates?", a: "Yes! We provide free, no-obligation estimates for all our services. Contact us to schedule a time that works for you." },
    { q: "Are your products safe for pets and children?", a: "Absolutely. We use eco-friendly, pet-safe products whenever possible. We'll always inform you about any products used and necessary precautions." },
    { q: "What areas do you serve?", a: "We serve Houston and surrounding areas including Katy, Sugar Land, The Woodlands, Pearland, Cypress, and more. Check our Service Areas page for the complete list." },
    { q: "How do I get started?", a: "Simply fill out our quote request form or call us at +1-771-1871-987. We'll schedule a visit to assess your property and provide a detailed quote." },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=2000&q=80')` }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-white animate-fade-in">
              <div className="flex items-center gap-2 mb-6">
                <span className="trust-badge bg-white/10 text-white backdrop-blur-sm">
                  <Shield className="w-4 h-4" />
                  Licensed & Insured
                </span>
                <span className="trust-badge bg-white/10 text-white backdrop-blur-sm">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  5-Star Rated
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
                Professional Lawn Care for a <span className="text-emerald-400">Greener</span>, Healthier Yard
              </h1>
              
              <p className="text-lg sm:text-xl text-emerald-100/90 mb-8 max-w-xl">
                Transform your outdoor space with Houston's trusted lawn care experts. Quality service, competitive prices, and a lawn you'll be proud of.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact">
                  <Button 
                    className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                    data-testid="hero-quote-btn"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+1-771-1871-987">
                  <Button 
                    variant="outline"
                    className="border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg transition-all duration-300"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Call Now
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center gap-6 text-emerald-100/80">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-emerald-600 border-2 border-white flex items-center justify-center text-sm font-medium">
                      {["JD", "SM", "RK", "AL"][i-1]}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm">Trusted by 500+ Houston homeowners</p>
                </div>
              </div>
            </div>
            
            {/* Right: Quote Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl animate-slide-in-right delay-200" data-testid="hero-quote-form">
              <h3 className="text-2xl font-heading font-bold text-forest-950 mb-2">
                Get Your Free Quote
              </h3>
              <p className="text-gray-600 mb-6">Fill out the form and we'll contact you within 24 hours.</p>
              <QuoteForm variant="compact" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Our Services</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-forest-950 mt-3 mb-6">
              Complete Lawn Care Solutions
            </h2>
            <p className="text-gray-600 text-lg">
              From regular maintenance to specialized treatments, we offer everything your lawn needs to thrive.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="service-card group bg-white rounded-2xl border border-emerald-100 p-8 hover:shadow-xl"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
                  <service.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-forest-950 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
                >
                  Learn More <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="view-all-services-btn"
              >
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-emerald-50/50" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Why Choose Nexivas</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-forest-950 mt-3 mb-6">
                Houston's Most Trusted Lawn Care Experts
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                With years of experience serving Houston homeowners and businesses, we've built a reputation for quality, reliability, and exceptional customer service.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-forest-950 mb-1">{benefit.title}</h4>
                      <p className="text-gray-600 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/about" className="inline-block mt-8">
                <Button 
                  variant="outline" 
                  className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-full px-8 py-4 transition-all duration-300"
                  data-testid="learn-more-btn"
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1630311853242-edd0c07766c0?auto=format&fit=crop&w=800&q=80"
                alt="Professional lawn care"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-forest-950">500+</p>
                    <p className="text-gray-600 text-sm">Happy Customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-forest-950 mt-3 mb-6">
              What Our Customers Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="testimonial-card"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-forest-950">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Work Gallery */}
      <section className="section-padding bg-emerald-50/50" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Our Work</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-forest-950 mt-3 mb-6">
              See the Nexivas Difference
            </h2>
            <p className="text-gray-600 text-lg">
              From perfectly manicured lawns to beautiful landscaping, see the results we deliver for our customers.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { src: "https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=600&q=80", alt: "Lush green lawn" },
              { src: "https://images.unsplash.com/photo-1630311853242-edd0c07766c0?auto=format&fit=crop&w=600&q=80", alt: "Professional lawn mowing" },
              { src: "https://images.unsplash.com/photo-1526392587392-d1627b6c134a?auto=format&fit=crop&w=600&q=80", alt: "Green grass field" },
              { src: "https://images.pexels.com/photos/280222/pexels-photo-280222.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Beautiful home lawn" },
              { src: "https://images.unsplash.com/photo-1756428785435-c3a6b74147d7?auto=format&fit=crop&w=600&q=80", alt: "Manicured lawn pattern" },
              { src: "https://images.unsplash.com/photo-1764296377273-4299e3f3d6f5?auto=format&fit=crop&w=600&q=80", alt: "Garden lawn bench" },
              { src: "https://images.unsplash.com/photo-1767334996666-e288d67821c2?auto=format&fit=crop&w=600&q=80", alt: "Beautiful garden landscaping" },
              { src: "https://images.unsplash.com/photo-1759577085348-7b7d9fb537a4?auto=format&fit=crop&w=600&q=80", alt: "Colorful flower bed" },
              { src: "https://images.unsplash.com/photo-1760627588647-5d2e2c15343e?auto=format&fit=crop&w=600&q=80", alt: "Garden flowers" },
              { src: "https://images.pexels.com/photos/8076031/pexels-photo-8076031.jpeg?auto=compress&cs=tinysrgb&w=600", alt: "Aerial suburban gardens" },
            ].map((image, index) => (
              <div 
                key={index}
                className="gallery-item group aspect-square"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="w-full h-full object-cover rounded-xl group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-end p-4">
                  <p className="text-white text-sm font-medium">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-4"
                data-testid="view-services-gallery-btn"
              >
                View Our Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-forest-950 text-white" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-400 font-accent font-medium uppercase tracking-wider text-sm">Service Areas</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mt-3 mb-6">
                Serving Greater Houston
              </h2>
              <p className="text-emerald-100/80 text-lg mb-8">
                From downtown Houston to the surrounding suburbs, we bring professional lawn care services to your neighborhood.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                {["Houston", "Katy", "Sugar Land", "The Woodlands", "Pearland", "Cypress", "Spring", "Richmond"].map((area) => (
                  <span key={area} className="area-tag bg-emerald-900/50 text-emerald-100 border border-emerald-700">
                    {area}
                  </span>
                ))}
              </div>
              
              <Link to="/service-areas">
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-8 py-4"
                  data-testid="view-areas-btn"
                >
                  View All Service Areas
                </Button>
              </Link>
            </div>
            
            <div className="map-container h-80 lg:h-96">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443086.5350818498!2d-95.68166854999999!3d29.8169295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Areas Map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">FAQ</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-forest-950 mt-3 mb-6">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="faq-item bg-white px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-forest-950 hover:text-emerald-600 py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-emerald-50/50" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-600 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Ready for a Better Lawn?
            </h2>
            <p className="text-emerald-100 text-lg max-w-2xl mx-auto mb-8">
              Join hundreds of satisfied Houston homeowners. Get your free quote today and see the Nexivas difference.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
                  data-testid="final-quote-btn"
                >
                  Get Your Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+1-771-1871-987">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
                  data-testid="final-call-btn"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  +1-771-1871-987
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
